import { customElement, scss } from '../../core/ids-decorators';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { stripTags } from '../../utils/ids-xss-utils/ids-xss-utils';
import type { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';
import '../ids-text/ids-text';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsValidationMixin from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import IdsDirtyTrackerMixin from '../../mixins/ids-dirty-tracker-mixin/ids-dirty-tracker-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsElement from '../../core/ids-element';
import type IdsRadio from './ids-radio';

import styles from './ids-radio-group.scss';

const Base = IdsDirtyTrackerMixin(
  IdsValidationMixin(
    IdsLocaleMixin(
      IdsLabelPositionMixin(
        IdsEventsMixin(
          IdsElement
        )
      )
    )
  )
);

/**
 * IDS Radio Group Component
 * @type {IdsRadioGroup}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @mixes IdsValidationMixin
 * @mixes IdsDirtyTrackerMixin
 */
@customElement('ids-radio-group')
@scss(styles)
export default class IdsRadioGroup extends Base {
  /**
   * Call the constructor and then initialize
   */
  constructor() {
    super();
  }

  isFormComponent = true;

  checked: any = null;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.DISABLED,
      attributes.HORIZONTAL,
      attributes.LABEL,
      attributes.LABEL_REQUIRED,
      attributes.LABEL_STATE,
      attributes.LANGUAGE,
      attributes.VALIDATE,
      attributes.VALIDATION_EVENTS,
      attributes.VALUE,
    ];
  }

  /**
   * Custom Element `connectedCallback` implementation
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();

    const slot = this.shadowRoot?.querySelector('slot:not([name])');
    this.onEvent('slotchange', slot, () => {
      this.afterChildrenReady();
    });

    this.isRadioGroup = true;
    this.attachInternalEventHandlers();
    this.dirtyContainer = this.shadowRoot?.querySelector('.ids-radio-group');
    this.handleDirtyTracker();
    this.setDirtyTracker();

    this.#inFlexObserver?.disconnect();
    this.#inFlexObserver = new MutationObserver(() => this.handleInFlexItem());
    this.#inFlexObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
    this.handleInFlexItem();

    // Establish the inline (side) label layout based on the group's own width
    this.onLabelPositionChange();
  }

  #inFlexObserver: MutationObserver | null = null;

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#inFlexObserver?.disconnect();
    this.#inFlexObserver = null;
    this.#labelPositionRo?.disconnect();
    this.#labelPositionRo = undefined;
  }

  /**
   * Watches the group's own size so the inline label layout responds to the
   * width of its container rather than the viewport.
   */
  #labelPositionRo?: ResizeObserver;

  /**
   * Width (px) at which an `inline-start` label switches from stacked to side-by-side.
   */
  #inlineStartBreakpoint = 320;

  /**
   * Width (px) at which an `inline-end` label switches from stacked to side-by-side.
   */
  #inlineEndBreakpoint = 767;

  /**
   * Reacts to `label-position` changes. The radio group positions its label via
   * CSS keyed on helper classes, which this handler toggles based on the group's
   * measured width. A ResizeObserver keeps the layout in sync as the container
   * (not the viewport) resizes.
   * @returns {void}
   */
  onLabelPositionChange(): void {
    const isInline = this.labelPosition === 'inline-start' || this.labelPosition === 'inline-end';

    if (isInline) {
      if (!this.#labelPositionRo) {
        this.#labelPositionRo = new ResizeObserver(() => this.#refreshInlineLabelLayout());
        this.#labelPositionRo.observe(this);
      }
    } else {
      this.#labelPositionRo?.disconnect();
      this.#labelPositionRo = undefined;
    }

    this.#refreshInlineLabelLayout();
  }

  /**
   * Toggles the inline label layout classes based on the group's own width.
   * @returns {void}
   */
  #refreshInlineLabelLayout(): void {
    const group = this.shadowRoot?.querySelector('.ids-radio-group');
    if (!group) return;

    const position = this.labelPosition;
    const width = this.getBoundingClientRect().width;
    let inline = false;
    let inlineEndStacked = false;

    if (position === 'inline-start') {
      inline = width > this.#inlineStartBreakpoint;
    } else if (position === 'inline-end') {
      // `inline-end` with a `label-break` keeps the wider inline layout,
      // matching the dedicated label-break rules.
      if (this.hasAttribute(attributes.LABEL_BREAK)) {
        inline = width > this.#inlineStartBreakpoint;
      } else if (width > this.#inlineEndBreakpoint) {
        inline = true;
      } else {
        inlineEndStacked = true;
      }
    }

    group.classList.toggle('label-inline', inline);
    group.classList.toggle('label-inline-end-stacked', inlineEndStacked);
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    // Radio
    const disabled = stringToBool(this.disabled) ? ' disabled' : '';
    const disabledAria = stringToBool(this.disabled) ? ' aria-disabled="true"' : '';
    const horizontal = stringToBool(this.horizontal) ? ' horizontal' : '';
    const labelStateClass = this.labelState ? ` label-state-${this.labelState}` : '';
    const rootClass = ` class="ids-radio-group${disabled}${horizontal}${labelStateClass}"`;
    const rInd = !(stringToBool(this.labelRequired) || this.labelRequired === null);
    const labelClass = ` class="group-label-text${rInd ? ' no-required-indicator' : ''}"`;

    // Label and accessible name
    const labelId = 'ids-radio-group-label';
    const label = `<ids-text type="legend"${labelClass}${disabledAria} id="${labelId}"><slot name="label"></slot></ids-text>`;
    const ariaLabelledby = this.label ? ` aria-labelledby="${labelId}"` : '';

    return `<div role="radiogroup"${rootClass}${disabledAria}${ariaLabelledby}>${label}<slot></slot></div>`;
  }

  /**
   * Get child ids-radio buttons in this group
   * @returns {IdsRadio[]} list of radios
   */
  get radios(): IdsRadio[] {
    return [...this.querySelectorAll<IdsRadio>('ids-radio')];
  }

  /**
   * Get the selected child ids-radio button in this group
   * @returns {IdsRadio[]} list of selected radios
   */
  get radiosSelected(): IdsRadio[] {
    return [...this.querySelectorAll<IdsRadio>('ids-radio[checked]')];
  }

  get input(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.ids-radio-group') ?? null;
  }

  get labelEl(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>('.group-label-text') ?? null;
  }

  /**
   * Set after children ready
   * @private
   * @returns {void}
   */
  afterChildrenReady(): void {
    this.setValue();
    this.handleHorizontal();
    this.handleDisabled();
    this.handleInFlexItem();
    this.attachInternalEventHandlers();
    this.handleValidation();
    this.setDirtyTracker();
  }

  /**
   * Propagate the `in-flex-item` class (set by ids-layout-flex-item on slotted
   * children) down to each child radio so the radio's shadow DOM can react.
   * @returns {void}
   */
  handleInFlexItem(): void {
    const inFlex = this.classList.contains('in-flex-item');
    this.radios.forEach((r: IdsRadio) => {
      if (inFlex) r.setAttribute('in-flex-item', 'true');
      else r.removeAttribute('in-flex-item');
    });
  }

  /**
   * Set value for group
   * @private
   * @returns {void}
   */
  setValue(): void {
    const defaultValue = this.value;
    const radios = this.radios;
    const radiosSelected = this.radiosSelected;

    if (!defaultValue && !radiosSelected.length) {
      const firstRadio = radios[0];
      firstRadio?.shadowRoot?.querySelector('.ids-radio')?.setAttribute('tabindex', '0');
      return;
    }

    const lastSelected = radiosSelected?.at(-1);
    const currentSelected = radios.find((radio) => (defaultValue && radio.value === defaultValue)) || lastSelected;

    radios.forEach((radio: IdsRadio) => {
      if (radio === currentSelected) {
        this.setAttribute(attributes.VALUE, radio?.value ?? '');
        radio.setAttribute(attributes.CHECKED, 'true');
      } else {
        // radio.checked = false; // For some reason this messes up the checked setter.
        radio.removeAttribute(attributes.CHECKED);
      }
    });
  }

  /**
   * Clear the group as checked, validation etc.
   * @returns {void}
   */
  clear(): void {
    this.value = null;
    this.checked = null;
    this.removeAllValidationMessages();
    this.resetDirtyTracker();
    const radio = this.querySelector<IdsRadio>('ids-radio');
    const rootEl = radio?.shadowRoot?.querySelector('.ids-radio');
    rootEl?.setAttribute('tabindex', '0');
  }

  /**
   * Set disabled for each radio in group.
   * @returns {void}
   */
  handleDisabled(): void {
    const radioArr = this.radios;
    const rootEl = this.shadowRoot?.querySelector('.ids-radio-group');

    if (stringToBool(this.disabled)) {
      this.labelEl?.setAttribute('aria-disabled', 'true');
      rootEl?.classList.add(attributes.DISABLED);
      radioArr.forEach((r: any) => r.setAttribute(attributes.GROUP_DISABLED, true));
    } else {
      this.labelEl?.removeAttribute('aria-disabled');
      rootEl?.classList.remove(attributes.DISABLED);
      radioArr.forEach((r: any) => r.removeAttribute(attributes.GROUP_DISABLED));
    }
  }

  /**
   * Set horizontal for each radio in group.
   * @returns {void}
   */
  handleHorizontal(): void {
    const radioArr = this.radios;
    const rootEl = this.shadowRoot?.querySelector('.ids-radio-group');
    if (stringToBool(this.horizontal)) {
      rootEl?.classList.add(attributes.HORIZONTAL);
      radioArr.forEach((r: any) => r.setAttribute(attributes.HORIZONTAL, true));
    } else {
      rootEl?.classList.remove(attributes.HORIZONTAL);
      radioArr.forEach((r: any) => r.removeAttribute(attributes.HORIZONTAL));
    }
  }

  /**
   * Make given radio as checked.
   * @private
   * @param {object} radio to make checked
   * @returns {void}
   */
  makeChecked(radio: IdsRadio): void {
    const value = radio?.value ?? '';
    this.value = value;

    this.checked = radio ?? false;
    if (radio) radio.setAttribute(attributes.CHECKED, 'true');

    const args = { bubbles: true, detail: { value, checked: radio ?? false } };
    this.triggerEvent('change', this.input, args);
    this.triggerEvent('change', this, args);
  }

  /**
   * Handle radio group change event
   * @private
   * @returns {void}
   */
  attachRadioGroupChangeEvent(): void {
    const radioArr = this.radios;

    radioArr.forEach((radio) => {
      this.offEvent(`change.radio-change`, radio);
      this.onEvent(`change.radio-change`, radio, () => {
        this.makeChecked(radio);
      });
    });
  }

  /**
   * Handle keydown event
   * @private
   * @returns {void}
   */
  attachRadioGroupKeydown(): void {
    const activeRadios = [...this.querySelectorAll<IdsRadio>('ids-radio:not([disabled="true"])')];
    const numActive = activeRadios.length;
    activeRadios.forEach((radio, index) => {
      this.offEvent('keydown', radio);
      this.onEvent('keydown', radio, (evt: KeyboardEvent) => {
        const allowedKeys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Space'];
        const key = evt.code;
        if (allowedKeys.includes(key)) {
          let nextIndex = index;
          if (key === 'ArrowDown' || key === 'ArrowRight') {
            nextIndex = (index >= (numActive - 1)) ? 0 : (nextIndex + 1);
          } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
            nextIndex = (index <= 0) ? (numActive - 1) : (nextIndex - 1);
          }
          this.makeChecked(activeRadios[nextIndex]);
          activeRadios[nextIndex].focus();
          evt.preventDefault();
        }
      });
    });
  }

  /**
   * Handle events
   * @private
   * @returns {void}
   */
  attachInternalEventHandlers(): void {
    this.attachRadioGroupChangeEvent();
    this.attachRadioGroupKeydown();
  }

  /**
   * Sets checkbox to disabled
   * @param {boolean | string} value If true will set `disabled` attribute
   */
  set disabled(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.DISABLED, value.toString());
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
    this.handleDisabled();
  }

  get disabled(): boolean | string { return stringToBool(this.getAttribute(attributes.DISABLED)); }

  /**
   * Flips the checkbox orientation to horizontal
   * @param {boolean|string} value If true will set `horizontal` attribute
   */
  set horizontal(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.HORIZONTAL, value.toString());
    } else {
      this.removeAttribute(attributes.HORIZONTAL);
    }
    this.handleHorizontal();
  }

  get horizontal() { return stringToBool(this.getAttribute(attributes.HORIZONTAL)); }

  /**
   * Set the `label` text
   * @param {string} value of the `label` text property
   */
  set label(value: string | null) {
    const rootEl = this.shadowRoot?.querySelector('.ids-radio-group');
    const labelText = this.shadowRoot?.querySelector<HTMLElement>('.group-label-text');
    if (value) {
      this.setAttribute(attributes.LABEL, value);
      if (labelText) {
        labelText.textContent = value;
      }
      // Set aria-labelledby on the radiogroup
      rootEl?.setAttribute(htmlAttributes.ARIA_LABELLED_BY, 'ids-radio-group-label');
      return;
    }
    this.removeAttribute(attributes.LABEL);
    if (labelText) {
      labelText.textContent = '';
    }
    // Remove aria-labelledby when label is removed
    rootEl?.removeAttribute(htmlAttributes.ARIA_LABELLED_BY);
  }

  get label(): string | null { return this.getAttribute(attributes.LABEL); }

  /**
   * Sets the checkbox to required
   * @param {string | null} value The `label-required` attribute
   */
  set labelRequired(value: string | null) {
    const val = stringToBool(value);
    if (value) {
      this.setAttribute(attributes.LABEL_REQUIRED, value.toString());
    } else {
      this.removeAttribute(attributes.LABEL_REQUIRED);
    }
    this.labelEl?.classList[!val ? 'add' : 'remove']('no-required-indicator');
  }

  get labelRequired(): string | null { return this.getAttribute(attributes.LABEL_REQUIRED); }

  /**
   * List of valid label visibility states
   * @returns {Array<IdsLabelStateMode>} the available label states
   */
  labelStates = ['hidden', 'collapsed'];

  /**
   * Get the current label visibility state
   * @returns {IdsLabelStateMode} the current state of the group label's visibility
   */
  get labelState(): IdsLabelStateMode {
    return (this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode) || null;
  }

  /**
   * Set the label visibility state. Accepts `hidden` (label keeps its space but
   * is not visible) or `collapsed` (label is removed from layout). The label text
   * remains in the DOM so `aria-labelledby` keeps the group's accessible name.
   * @param {IdsLabelStateMode} value the type of label visibility to apply
   */
  set labelState(value: IdsLabelStateMode) {
    let safeValue: IdsLabelStateMode = null;
    if (typeof value === 'string') {
      safeValue = stripTags(value, '') as IdsLabelStateMode;
    }

    if (safeValue !== null && this.labelStates.includes(safeValue)) {
      this.setAttribute(attributes.LABEL_STATE, `${safeValue}`);
    } else {
      this.removeAttribute(attributes.LABEL_STATE);
    }

    this.#refreshLabelState();
  }

  /**
   * Toggle the `label-state-*` CSS class on the container to reflect the current state.
   * @private
   * @returns {void}
   */
  #refreshLabelState(): void {
    const rootEl = this.shadowRoot?.querySelector('.ids-radio-group');
    if (!rootEl) return;
    rootEl.classList.remove('label-state-hidden', 'label-state-collapsed');
    if (this.labelState) {
      rootEl.classList.add(`label-state-${this.labelState}`);
    }
  }

  /**
   * Matches the radio value to the group value and sets the checked attribute
   * @returns {void}
   */
  updateChecked() {
    this.radios?.forEach((radio) => {
      if (this.value && radio.value === this.value) {
        radio.setAttribute(attributes.CHECKED, 'true');
      } else {
        radio.removeAttribute(attributes.CHECKED);
      }
    });
  }

  /**
   * Sets the checkbox `value` attribute
   * @param {string | null} value the value property
   */
  set value(value: string | null) {
    if (!value) {
      this.removeAttribute(attributes.VALUE);
    } else {
      this.setAttribute(attributes.VALUE, value);
    }

    this.updateChecked();
  }

  get value(): string | null { return this.getAttribute(attributes.VALUE); }
}
